'use client';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import Slider from 'react-slick';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import guests from '../../../public/guests.png';

import { Village } from '@/services/types';
import { getVillageLink } from '@/utils/village';
import getMediaFilterUrl from '@/utils/get-media-filter';

const settings = {
  dots: false,
  infinite: true,
  speed: 1500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
};

type Props = {
  villages: Village[];
  lang: string;
};

type VillageCarouselCardProps = {
  village: Village;
  lang: string;
};

export function MapCarousel({ villages, lang }: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);

  const singleVillage = villages.length === 1;

  return (
    <>
      {singleVillage ? (
        <VillageCarouselCard lang={lang} village={villages[0]} />
      ) : (
        <div className="relative block max-w-[400px] w-[400px] max-h-[110px] overflow-hidden pr-[30px] pl-[50px] box-border">
          <button
            onClick={sliderRef?.slickPrev}
            className="absolute top-[50%] cursor-pointer left-[15px] translate-y-[-50%] outline-none">
            <FiChevronLeft className="w-10 h-10" />
          </button>
          <Slider ref={setSliderRef} {...settings}>
            {villages.map((village, i) => (
              <VillageCarouselCard lang={lang} village={village} key={i} />
            ))}
          </Slider>
          <button
            onClick={sliderRef?.slickNext}
            className="absolute top-[50%] cursor-pointer right-0 translate-y-[-50%] outline-none">
            <FiChevronRight className="w-10 h-10" />
          </button>
        </div>
      )}
    </>
  );
}

function VillageCarouselCard({ village, lang }: VillageCarouselCardProps) {
  const pathname = usePathname();
  const villageImgCover = village.media.filter(
    (medias) => medias.type == 'cover',
  )[0];

  const villageLink = getVillageLink({
    lang,
    slug: village.slug,
    region: village.zone.region.code,
  });

  const filterUrl = getMediaFilterUrl({
    mediaFilters: villageImgCover.mediaFilters,
    filterName: 'small_1-1',
  });

  return (
    <div className="block w-[320px] h-full min-h-[1px] float-left">
      <div className="flex max-w-[350px]">
        <div className="relative w-[100px] h-[100px]">
          <a title={village.title} target="_blank" href={villageLink}>
            <Image
              loading="lazy"
              className="w-full h-full block align-middle"
              src={
                filterUrl
                  ? filterUrl
                  : `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/small_1-1/${villageImgCover?.path}${villageImgCover?.filename}`
              }
              alt={village.title}
              fill={true}
            />
          </a>
        </div>
        <div className="block font-['Averta']">
          <div className="block text-lg font-medium mb-2 ml-4">
            <a title={village.title} target="_blank" href={villageLink}>
              {village.title}
            </a>
          </div>
          <div className="mb-8 flex flex-wrap items-center">
            <div className="mr-[10px] flex items-center">
              <Image
                className="mr-2 ml-4"
                src={guests}
                alt="Guests icon image"
              />
              <span className="text-base text-black font-normal">
                {village.sleeps}
              </span>
            </div>
            <div className="mr-[10px] flex items-center">
              <svg
                className="mr-2 ml-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="20"
                viewBox="0 0 24 20">
                <path
                  fill="#CCC"
                  fillRule="evenodd"
                  d="M22 10.444c1.737.768 2 1.556 2 1.556v8h-2v-2H2v2H0v-8s.263-.788 2-1.556V0h2v3h16V0h2v10.444zm-2-.666V5h-7v4.009c3.138.054 5.389.366 7 .769zm-16 0c1.611-.403 3.862-.715 7-.77V5H4v4.778zM22 16v-3s-2-2-10-2-10 2-10 2v3h20z"
                />
              </svg>

              <span className="text-base text-black font-normal">
                {village.bedrooms}
              </span>
            </div>
            <div className="mr-[10px] flex items-center">
              <svg
                className="mr-2 ml-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="20"
                viewBox="0 0 24 20">
                <path
                  fill="#CCC"
                  fillRule="nonzero"
                  d="M11 5.998a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM13 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM9 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm8 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm7 2v2h-2v1c0 1.89-1.235 3.584-3 4l1 2h-2l-1-2H7l-1 2H4l1-2c-1.765-.416-3-2.11-3-4v-1H0v-2h20V3c0-.551-.449-1-1-1h-5v1h-2V0h7c1.654 0 3 1.346 3 3v8h2zm-4 3v-1H4v1c0 1.102.897 2 2 2h12c1.103 0 2-.898 2-2z"
                />
              </svg>
              <span className="text-base text-black font-normal">
                {village.bathrooms}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
